<template>
  <section class="excedentes-page">
    <top-menu/>
    <section>
      <page-header headerName="Excedentes mensais" :showBtn="false" btnName="" btnRoute="" css="container-fluid"/>

      <div class="page-content container-fluid">

        <!-- ETAPA 1 -->
        <div class="step-box">
          <div class="step-header">
            <span class="badge">1</span>
            <h4>Gerar relatório inicial</h4>
          </div>
          <p>
            Gera a planilha CSV a partir da query mensal de excedentes (UNION
            entre todos os bancos <code>marqponto-prod*-db</code>). Clientes
            mensais não entram (reajustam sozinhos no ciclo). A planilha traz
            <code>MRR ATUAL</code> e <code>MRR ESPERADO</code> (pós excedente);
            no upload, cada boleto gerado também sobe o MRR da assinatura para o
            valor esperado. Faça o download, confira e ajuste na etapa 2.
          </p>
          <button class="btn-primary" :disabled="loadingInitial" @click="downloadInitial">
            <span v-if="!loadingInitial">Baixar planilha inicial (.csv)</span>
            <span v-else>Gerando...</span>
          </button>
          <p class="hint" v-if="lastInitialAt">Última geração: {{ lastInitialAt }}</p>
        </div>

        <!-- ETAPA 2 -->
        <div class="step-box">
          <div class="step-header">
            <span class="badge">2</span>
            <h4>Subir planilha conferida e gerar boletos</h4>
          </div>

          <div class="form-row">
            <label>Arquivo CSV conferido</label>
            <input type="file" accept=".csv" @change="onFileSelected"/>
            <p v-if="selectedFile" class="file-info">
              {{ selectedFile.name }} ({{ Math.round(selectedFile.size / 1024) }} KB)
            </p>
          </div>

          <div class="form-row inline">
            <label>Data de vencimento (única para todos)</label>
            <input type="date" v-model="dueDate"/>
          </div>

          <p class="hint">
            Os boletos são emitidos no Asaas. A conta (BOH ou MARQ) é escolhida
            automaticamente pelo <code>BillingTarget</code> de cada empresa.
          </p>

          <button class="btn-primary"
                  :disabled="!canProcess || processing"
                  @click="process">
            <span v-if="!processing">Processar e gerar boletos</span>
            <span v-else>Processando...</span>
          </button>
        </div>

        <!-- ETAPA 3 -->
        <div class="step-box" v-if="lastBatch">
          <div class="step-header">
            <span class="badge">3</span>
            <h4>Resultado e planilha final</h4>
          </div>

          <ul class="summary">
            <li><strong>Batch:</strong> {{ lastBatch.batchId }}</li>
            <li><strong>Total:</strong> {{ lastBatch.totalRows }}</li>
            <li class="ok"><strong>Sucesso:</strong> {{ lastBatch.successRows }}</li>
            <li class="err"><strong>Falhas:</strong> {{ lastBatch.errorRows }}</li>
          </ul>

          <button class="btn-primary" @click="downloadFinal(lastBatch.batchId)">
            Baixar planilha final (.csv)
          </button>

          <table class="result-table" v-if="lastBatch.rows && lastBatch.rows.length">
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Excedentes</th>
                <th>Valor</th>
                <th>MRR (atual → esperado)</th>
                <th>Status</th>
                <th>MRR status</th>
                <th>Boleto</th>
                <th>Erro</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in lastBatch.rows" :key="i"
                  :class="{ 'row-ok': r.boletoStatus === 'OK', 'row-err': r.boletoStatus === 'FALHA' }">
                <td>{{ r.companyName }}</td>
                <td>{{ r.excedentes }}</td>
                <td>R$ {{ r.valor }}</td>
                <td>R$ {{ r.mrrAtual }} → R$ {{ r.mrrEsperado }}</td>
                <td>{{ r.boletoStatus }}</td>
                <td>{{ r.mrrStatus }}</td>
                <td><a v-if="r.boletoUrl" :href="r.boletoUrl" target="_blank">Abrir</a></td>
                <td class="err-cell">{{ r.erro }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- HISTÓRICO -->
        <div class="step-box">
          <div class="step-header">
            <h4>Histórico de execuções</h4>
          </div>
          <table class="history-table" v-if="history.length">
            <thead>
              <tr>
                <th>Quando</th>
                <th>Master</th>
                <th>Gateway</th>
                <th>Vencimento</th>
                <th>Total</th>
                <th>OK</th>
                <th>Falhas</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="h in history" :key="h.batchId">
                <td>{{ formatDate(h.processedAt) }}</td>
                <td>{{ h.processedBy }}</td>
                <td>{{ h.gateway }}</td>
                <td>{{ formatDate(h.dueDate) }}</td>
                <td>{{ h.totalRows }}</td>
                <td class="ok">{{ h.successRows }}</td>
                <td class="err">{{ h.errorRows }}</td>
                <td><button class="btn-link" @click="downloadFinal(h.batchId)">Baixar final</button></td>
              </tr>
            </tbody>
          </table>
          <p v-else class="empty">Nenhuma execução registrada ainda.</p>
        </div>
      </div>
    </section>
  </section>
</template>

<script>
import {
  EXCEDENTES_GENERATE_INITIAL,
  EXCEDENTES_UPLOAD_AND_GENERATE_BOLETOS,
  EXCEDENTES_DOWNLOAD_FINAL,
  EXCEDENTES_HISTORY
} from "../../../../../urlConfig";
import { REQUEST_SERVICE } from "../../../../../requestConfig";
import { UTIL_SERVICE } from "../../../../../utils";
import topMenu from "../../../components/default/topMenu.vue";
import pageHeader from "../../../components/default/pageHeader.vue";
import swal from "sweetalert";

export default {
  name: "excedentes",
  components: { topMenu, pageHeader },
  data() {
    return {
      loadingInitial: false,
      lastInitialAt: null,

      selectedFile: null,
      dueDate: "",
      processing: false,

      lastBatch: null,
      history: []
    };
  },
  computed: {
    canProcess() {
      return this.selectedFile && this.dueDate;
    }
  },
  beforeMount() {
    this.loadHistory();
  },
  methods: {
    // ===== Etapa 1 =====
    downloadInitial() {
      this.loadingInitial = true;
      window.open(EXCEDENTES_GENERATE_INITIAL, "_blank");
      this.loadingInitial = false;
      this.lastInitialAt = new Date().toLocaleString("pt-BR");
    },

    // ===== Etapa 2 =====
    onFileSelected(event) {
      this.selectedFile = event.target.files[0] || null;
    },
    // Sem async/await de proposito: este build do Master nao carrega o
    // regeneratorRuntime, entao o async quebrava aqui ("regeneratorRuntime is
    // not defined"). Promises encadeadas seguem o padrao do resto do arquivo.
    process() {
      if (!this.canProcess || this.processing) return;
      this.processing = true;
      this.parseCsv(this.selectedFile)
        .then((rows) => {
          const payload = {
            dueDate: this.dueDate,
            rows
          };
          return REQUEST_SERVICE.requestContext(
            "POST",
            EXCEDENTES_UPLOAD_AND_GENERATE_BOLETOS,
            null,
            payload
          );
        })
        .then((response) => {
          this.lastBatch = response.data;
          UTIL_SERVICE.console("RESULT EXCEDENTES PROCESS:", response.data);
          swal(
            "Concluído",
            `${response.data.successRows} boleto(s) gerado(s), ${response.data.errorRows} falha(s).`,
            response.data.errorRows > 0 ? "warning" : "success"
          );
          this.loadHistory();
        })
        .catch(() => {
          swal("Aviso", "Falha ao processar a planilha. Verifique o formato.", "error");
        })
        .then(() => {
          this.processing = false;
        });
    },

    // ===== Etapa 3 =====
    downloadFinal(batchId) {
      window.open(`${EXCEDENTES_DOWNLOAD_FINAL}?batchId=${batchId}`, "_blank");
    },

    // ===== Histórico =====
    loadHistory() {
      REQUEST_SERVICE.get(EXCEDENTES_HISTORY)
        .then((r) => { this.history = r.data || []; })
        .catch(() => { this.history = []; });
    },

    formatDate(isoDate) {
      if (!isoDate) return "";
      return new Date(isoDate).toLocaleString("pt-BR");
    },

    // Parsing CSV simples (separador ;)
    parseCsv(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (evt) => {
          try {
            const text = evt.target.result;
            const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
            if (lines.length < 2) return resolve([]);

            const rows = lines.slice(1).map((line, idx) => {
              const cells = this.splitCsvLine(line);
              if (cells.length < 15) {
                throw new Error("Linha " + (idx + 2) + " com colunas a menos (" + cells.length + "/15). Não altere a estrutura do CSV.");
              }
              return {
                companyId:               cells[0]  || "",
                masterUrl:               cells[1]  || "",
                companyName:             cells[2]  || "",
                periodoCobranca:         cells[3]  || "",
                vencimentoContrato:      cells[4]  || "",
                mesesParaVencer:         this.parseIntSafe(cells[5]),
                quantidadeNoBackoffice:  this.parseIntSafe(cells[6]),
                maximoNoContrato:        this.parseIntSafe(cells[7]),
                valorPorFuncionario:     this.parseNum(cells[8]),
                excedentes:              this.parseIntSafe(cells[9]),
                valor:                   this.parseNum(cells[10]),
                faturamento:             cells[11] || "",
                databaseName:            cells[12] || "",
                mrrAtual:                this.parseNum(cells[13]),
                mrrEsperado:             this.parseNum(cells[14])
              };
            });
            resolve(rows);
          } catch (e) {
            reject(e);
          }
        };
        reader.onerror = reject;
        reader.readAsText(file, "UTF-8");
      });
    },
    // Decimal robusto a pt-BR: aceita "1234.56" (InvariantCulture do backend) e
    // "1.234,56" (reescrito pelo Excel pt-BR), sem truncar silenciosamente.
    parseNum(raw) {
      if (raw === undefined || raw === null) return 0;
      let s = String(raw).trim();
      if (s === "") return 0;
      if (s.indexOf(",") !== -1) s = s.replace(/\./g, "").replace(",", ".");
      const n = parseFloat(s);
      return isNaN(n) ? 0 : n;
    },
    parseIntSafe(raw) {
      if (raw === undefined || raw === null) return 0;
      const s = String(raw).trim().replace(/[.,]/g, "");
      const n = parseInt(s, 10);
      return isNaN(n) ? 0 : n;
    },
    splitCsvLine(line) {
      // splitter mínimo que respeita aspas duplas
      const result = [];
      let current = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') { inQuotes = !inQuotes; continue; }
        if (ch === ";" && !inQuotes) { result.push(current); current = ""; continue; }
        current += ch;
      }
      result.push(current);
      return result;
    }
  }
};
</script>

<style lang="scss" scoped>
.excedentes-page {
  .page-content { padding: 24px; }

  .step-box {
    background: #fff;
    border-radius: 6px;
    padding: 20px;
    margin-bottom: 18px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06);

    .step-header {
      display: flex; align-items: center; gap: 10px; margin-bottom: 12px;
      .badge {
        background: #3598dc; color: #fff; border-radius: 50%;
        width: 28px; height: 28px; display: inline-flex; justify-content: center;
        align-items: center; font-weight: bold;
      }
      h4 { margin: 0; font-weight: bold; }
    }
    p { color: #666; font-size: 13px; }
    .hint { color: #888; font-size: 12px; margin-top: 6px; }

    .form-row { margin: 12px 0;
      label { font-weight: 500; font-size: 13px; display: block; margin-bottom: 4px; }
      input[type="date"], select {
        border: 1px solid #ccc; border-radius: 4px; padding: 6px 10px; max-width: 260px;
      }
      .file-info { font-size: 13px; color: #444; margin-top: 6px; }
    }
    .form-row.inline { display: inline-block; margin-right: 24px; }

    .btn-primary {
      background: #3598dc; color: #fff; padding: 8px 22px; border-radius: 4px;
      border: none; cursor: pointer; font-weight: 600;
    }
    .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  }

  .summary {
    list-style: none; padding: 0; margin: 0 0 16px 0;
    li { padding: 4px 0; font-size: 13px; }
    .ok { color: #27ae60; }
    .err { color: #c0392b; }
  }

  .result-table, .history-table {
    width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 12px;
    th, td { padding: 8px; border-bottom: 1px solid #eee; text-align: left; vertical-align: middle; }
    th { color: #555; font-weight: 600; background: #fafafa; }
    .row-ok td { background: #f0fbf3; }
    .row-err td { background: #fdf3f3; }
    .err-cell { color: #c0392b; font-size: 12px; }
    .ok { color: #27ae60; }
    .err { color: #c0392b; }
    a { color: #3598dc; }
    .btn-link {
      background: none; border: none; color: #3598dc; cursor: pointer; padding: 0; font-size: 12px;
    }
  }

  .empty { color: #999; text-align: center; padding: 20px 0; }
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/payment/excedentes/excedentes.vue